import { FC, useContext, useMemo, useState } from "react";
import { useRecoilValue, useSetRecoilState } from "recoil";
import Accordion from "~/components/Accordion";
import Divider from "~/components/Divider";
import Icon from "~/components/Icon";
import InputDiscount from "~/components/InputDiscount";
import CouponItem from "~/components/Items/CouponItem";
import SummaryItem from "~/components/Items/SummaryItem";
import PaymentMethod from "~/components/PaymentMethod";
import RadioGroup from "~/components/Radio/RadioGroup";
import Text from "~/components/Text";
import { CheckoutContext } from "~/contexts/checkout.context";
import { Payment } from "~/services/payment";
import ShippingInfo from "../ShippingInfo";
import { trackingPaymentMethodChange } from "~/services/tracking";
import { useCart } from "~/data/cart";
import { Cart, CartAtom } from "~/services/cart";
import cartAtom from "~/services/cart/cart.atom";
import { Coupon } from "~/services/coupon";
import { isEmpty } from "lodash";
import Skeleton from "react-loading-skeleton";
import CheckoutSummary from "../CheckoutSummary";
import PaymentStripe from "~/components/PaymentStripe";
import { formatNumber } from "~/utils/number";
import Textarea from "~/components/TextArea";
import Link from "next/link";
import { PATH } from "~/constants/enum";

interface MobileCheckoutProps {
  formik: any;
  isShowShipping: boolean;
  shippingMethodOptions: any[];
  handleSelectShippingMethod: (e: any) => void;
  handleSelectPaymentMethod: (key: string, value: string) => void;
  loadingBilling?: boolean;
  onChangeElement?: any;
}

const MobileCheckout: FC<MobileCheckoutProps> = ({
  formik,
  isShowShipping,
  loadingBilling,
  onChangeElement,
  shippingMethodOptions,
  handleSelectPaymentMethod,
  handleSelectShippingMethod,
}) => {
  const cartSelector = useRecoilValue(CartAtom.cartSelector);
  const setCart = useSetRecoilState(cartAtom.cart);
  const [errorPaymentStripe, setErrorPaymentStripe] = useState<any>();

  // const { formik, handleAddCoupon, handleRemoveCoupon } =
  //   useContext(CheckoutContext);
  const { removeCouponCode } = useCart();
  const [isShowMore, setIsShowMore] = useState(false);

  const displayList = useMemo(() => {
    if (isShowMore) {
      return cartSelector?.items;
    }

    return cartSelector?.items?.slice(0, 2);
  }, [isShowMore, cartSelector?.items]);

  return (
    <>
      {!errorPaymentStripe && (
        <>
          <div className="bg-white rounded-xl overflow-hidden p-2 ">
            <PaymentStripe onError={(err) => setErrorPaymentStripe(err)} />
          </div>
          <Divider text="OR" className="my-2" />
        </>
      )}

      <Accordion
        variant="filled"
        className="w-full lg:!hidden"
        defaultActive={[
          "shipping-information",
          "shipping-methods",
          "payment-methods",
          "payment-stripe",
        ]}
        items={[
          {
            label: "Order Summary",
            key: "order-summary",
            icon: "bag-tick",
            extra: (
              <Text type="title-2">
                ${formatNumber(cartSelector?.total, 2)}
              </Text>
            ),
            content: (
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2">
                  <div className="flex items-center justify-between">
                    <Text type="label-3" className="text-grey-900">
                      Products ({cartSelector?.items?.length})
                    </Text>
                    <Link
                      href={{
                        pathname: PATH.Carts,
                      }}
                      className="underline text-sm"
                    >
                      Edit
                    </Link>
                  </div>
                  {displayList?.map((item) => (
                    <SummaryItem key={item.id} cartItem={item} />
                  ))}
                  {Number(cartSelector?.items?.length) > 2 && (
                    <Text
                      className="self-start px-4 flex items-center gap-2"
                      onClick={() => setIsShowMore(!isShowMore)}
                    >
                      {isShowMore ? "Show less" : "Show more"}{" "}
                      <Icon
                        size={18}
                        name={isShowMore ? "arrow-up-2" : "arrow-down-1"}
                      />
                    </Text>
                  )}
                </div>
                <Divider dividerClassName="border-dashed" />
                <div className="">
                  <CheckoutSummary className="!p-0" />
                </div>
              </div>
            ),
          },
          {
            label: "Shipping Information",
            key: "shipping-information",
            icon: "user-square",
            content: (
              <div className="flex flex-col gap-4">
                <ShippingInfo formik={formik} />
              </div>
            ),
          },
          {
            label: "Shipping Methods",
            key: "shipping-methods",
            icon: "box",
            content: (
              <div id={"selected_shipping_method"} className="flex flex-col">
                {!isEmpty(shippingMethodOptions) && isShowShipping ? (
                  <RadioGroup
                    value={formik.values.selected_shipping_method?.carrier_code}
                    onChange={(e) => handleSelectShippingMethod(e)}
                    // onChange={(e) => {
                    //   trackingShippingMethodChange(e.target.value);
                    //   formik.setFieldValue("shipping_method", e.target.value);
                    // }}
                    options={shippingMethodOptions}
                  />
                ) : loadingBilling ? (
                  <Skeleton
                    containerClassName="flex-1 text-right"
                    width="100%"
                  />
                ) : isEmpty(shippingMethodOptions) && isShowShipping ? (
                  <span className={"text-error"}>
                    No shipping method available for this address. Please update
                    your address.
                  </span>
                ) : (
                  "Shipping charge will be calculated once address is updated"
                )}
                {!!shippingMethodOptions?.length &&
                  formik.errors["selected_shipping_method"] && (
                    <span className={"text-error"}>
                      {formik.errors["selected_shipping_method"].toString()}
                    </span>
                  )}
              </div>
            ),
          },
          {
            label: "Payment Methods",
            key: "payment-methods",
            icon: "card-pos",
            content: (
              <PaymentMethod
                payment={formik.values?.selected_payment_method}
                errors={
                  formik.errors?.selected_payment_method as Payment | undefined
                }
                touched={
                  formik.touched?.selected_payment_method as Payment | undefined
                }
                onChange={handleSelectPaymentMethod}
                formik={formik}
                onChangeElement={onChangeElement}
              />
            ),
          },
          {
            label: "ORDER NOTE",
            key: "note",
            icon: "note",
            content: (
              <Textarea
                placeholder="Enter your note"
                {...formik.getFieldProps?.("note")}
              />
            ),
          },
        ]}
      />
    </>
  );
};

export default MobileCheckout;
